/**
 * Runtime config for the SPA.
 *
 * The bundle is built ONCE and deployed to many environments (dev
 * laptop, staging, prod). Anything that differs per-environment must
 * not be compiled in. We fetch `/config.json` at startup, which is
 * served by the same origin as the SPA itself. In production the
 * nginx image substitutes values from env vars into config.json at
 * container boot (see apps/web/docker/nginx.sh).
 *
 * Everything in RuntimeConfig is public by definition — it ends up in
 * DevTools anyway. Never put secrets here.
 */
export interface RuntimeConfig {
  /** Base URL of the atlas API, e.g. "" (same-origin prod) or
   *  "http://localhost:3000" (dev with separate ports). No trailing slash. */
  apiBase: string;
  /** OIDC issuer URL, e.g. "https://crm.rise-ap.co.uk/auth/realms/atlas". */
  oidcIssuer: string;
  /** OIDC public client_id (the atlas-web public client). */
  oidcClientId: string;
  /** Fully-qualified redirect URI registered on the atlas-web client. */
  oidcRedirectUri: string;
  /** Fully-qualified post-logout redirect URI. */
  oidcPostLogoutRedirectUri: string;
}

let cached: RuntimeConfig | null = null;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  // Cache-bust because nginx will rewrite this file on every container
  // start, and we don't want the browser to hold on to a stale copy.
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`failed to load /config.json: ${res.status}`);
  }
  const raw = (await res.json()) as RuntimeConfig;
  // Normalise: strip trailing slash from apiBase.
  cached = {
    ...raw,
    apiBase: raw.apiBase.replace(/\/$/, ''),
  };
  return cached;
}
